import { Box, Typography } from "@mui/material";
import {
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Tooltip as ChartTooltip,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
} from "chart.js";
import React from "react";
import { Radar } from "react-chartjs-2";
import ChartWrapper from "./ChartWrapper";

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip,
  Legend
);

// Create a plugin to draw a dotted reference line at value 8 with a tooltip
const recommendedScorePlugin = {
  id: "recommendedScore",
  // Store state for tooltip
  _tooltipVisible: false,
  _mouseX: 0,
  _mouseY: 0,
  _tooltipText: "Recommended score: 8 or above",

  beforeInit(chart) {
    // Add mouse move listener to canvas
    chart.canvas.addEventListener("mousemove", (e) => {
      const rect = chart.canvas.getBoundingClientRect();
      this._mouseX = e.clientX - rect.left;
      this._mouseY = e.clientY - rect.top;

      // Check if mouse is near the dotted line
      this._checkTooltipVisibility(chart);
      chart.draw(); // Redraw to update tooltip
    });

    // Add mouse leave listener
    chart.canvas.addEventListener("mouseleave", () => {
      this._tooltipVisible = false;
      chart.draw(); // Redraw to hide tooltip
    });
  },

  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const scale = chart.scales.r;

    if (!scale) return;

    // Get the center of the chart
    const centerX = scale.xCenter;
    const centerY = scale.yCenter;

    // Get the radius for value 8
    const radius8 = scale.getDistanceFromCenterForValue(8);
    if (!radius8 || isNaN(radius8)) return;

    // Save the current canvas state
    ctx.save();

    // Draw the dotted reference line at value 8
    ctx.beginPath();
    ctx.setLineDash([3, 3]); // Create dotted line effect
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = "#5048e5"; // Purple color
    ctx.arc(centerX, centerY, radius8, 0, 2 * Math.PI);
    ctx.stroke();

    // Store radius and center for tooltip calculation
    this._centerX = centerX;
    this._centerY = centerY;
    this._radius = radius8;

    // Draw tooltip if visible
    if (this._tooltipVisible) {
      this._drawTooltip(chart, ctx);
    }

    // Restore the canvas state
    ctx.restore();
  },

  // Check if mouse is near the dotted line
  _checkTooltipVisibility(chart) {
    if (!this._centerX || !this._centerY || !this._radius) return;

    // Calculate distance from mouse to center
    const dx = this._mouseX - this._centerX;
    const dy = this._mouseY - this._centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Check if mouse is near the dotted line (within 10px)
    this._tooltipVisible = Math.abs(distance - this._radius) < 10;
  },

  // Draw custom tooltip
  _drawTooltip(chart, ctx) {
    // Position tooltip near mouse
    const x = this._mouseX;
    const y = this._mouseY - 30; // Position above cursor

    // Draw tooltip background
    ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    ctx.strokeStyle = "#e0e0e0";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x - 100, y - 20, 200, 30, 4);
    ctx.fill();
    ctx.stroke();

    // Draw tooltip text
    ctx.fillStyle = "#5048e5";
    ctx.font = "bold 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(this._tooltipText, x, y - 5);
  },
};

// Register our custom plugin
ChartJS.register(recommendedScorePlugin);

interface RadarChartProps {
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
    }[];
  };
  height?: string | number;
  width?: string | number;
  showLegend?: boolean;
  usePurpleColor?: boolean;
}

const RadarChart: React.FC<RadarChartProps> = ({
  data,
  height = "360px",
  width = "100%",
  showLegend,
  usePurpleColor = false,
}) => {
  // Format the chart data with styling
  const chartData: ChartData<"radar"> = {
    labels: data.labels,
    datasets: data.datasets.map((dataset, index) => {
      // Define colors for different datasets
      const colors = [
        { bg: "rgba(80, 72, 229, 0.1)", border: "rgba(80, 72, 229, 1)" }, // Purple
        { bg: "rgba(218, 0, 200, 0.1)", border: "rgba(218, 0, 200, 1)" }, // Pink
        { bg: "rgba(59, 130, 246, 0.2)", border: "rgba(59, 130, 246, 1)" }, // Blue
        { bg: "rgba(249, 115, 22, 0.2)", border: "rgba(249, 115, 22, 1)" }, // Orange
      ];

      // Use purple color for single article mode if specified
      const purpleColor = {
        bg: "rgba(80, 72, 229, 0.2)",
        border: "rgba(80, 72, 229, 1)",
      }; // Brand purple

      // If usePurpleColor is true, force purple color
      const colorIndex = usePurpleColor ? 0 : index % colors.length;
      const colorSet = usePurpleColor ? purpleColor : colors[colorIndex];

      return {
        ...dataset,
        fill: true,
        backgroundColor: colorSet.bg,
        borderColor: colorSet.border,
        pointBackgroundColor: colorSet.border,
        pointBorderColor: "#fff",
        pointRadius: 4,
      };
    }),
  };

  // Collect colors and labels for custom legend
  const legendItems = chartData.datasets.map((dataset, index) => ({
    label: dataset.label,
    color: dataset.borderColor as string,
    bg: dataset.backgroundColor as string,
  }));

  // Custom plugin for legend spacing (now handled separately from the dotted reference line)
  const legendSpacingPlugin = {
    id: "legendSpacing",
    afterInit: (chart) => {
      // This runs only once after chart initialization
    },
    afterRender: (chart) => {
      // Apply custom spacing to legend items after render
      setTimeout(() => {
        const legendItems = document.querySelectorAll(".chartjs-legend-item");
        legendItems.forEach((item) => {
          (item as HTMLElement).style.marginLeft = "25px";
          (item as HTMLElement).style.marginRight = "25px";
        });
      }, 0);
    },
  };

  // Register the legend spacing plugin
  ChartJS.register(legendSpacingPlugin);

  // Chart.js options
  const chartOptions: ChartOptions<"radar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide the built-in legend
      },
      tooltip: {
        enabled: true, // Keep regular tooltips for data points
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.formattedValue}`;
          },
        },
      },
      // Our custom plugins are registered globally with ChartJS
    },
    layout: {
      padding: {
        top: 10,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    scales: {
      r: {
        startAngle: 315,
        angleLines: {
          color: "#e0e0e0",
        },
        grid: {
          color: "#e0e0e0",
          circular: true,
        },
        beginAtZero: true,
        suggestedMin: 0,
        suggestedMax: 10,
        ticks: {
          stepSize: 2,
          font: {
            size: 12,
          },
          // No callback needed here, we'll use a plugin instead
        },
        pointLabels: {
          font: {
            size: 12,
            weight: "normal",
          },
          color: "#637381",
        },
      },
    },
    elements: {
      line: {
        tension: 0.4, // Gives slightly rounded corners
        borderWidth: 2,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
        borderWidth: 2,
      },
    },
  };

  return (
    <ChartWrapper height={height} width={width}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Radar
            data={chartData}
            options={{
              ...chartOptions,
              aspectRatio: 1,
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  display: false,
                },
              },
            }}
          />
        </Box>

        {showLegend && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              mt: 0.5,
              mb: 0,
              gap: "30px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {legendItems.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  mt: "-2.5rem",
                }}
              >
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    backgroundColor: item.bg,
                    borderRadius: 0.5,
                    border: `1px solid ${item.color}`,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#637381",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ChartWrapper>
  );
};

export default RadarChart;
